import { Link } from "react-router-dom";
import logoUnotif from "@/assets/logo-unotif.png";
import linkedinIcon from "@/assets/social/linkedin.svg";
import facebookIcon from "@/assets/social/facebook.svg";
import youtubeIcon from "@/assets/social/youtube.svg";
import emailIcon from "@/assets/social/email.svg";

export const Footer = () => {
  const productLinks = [
    { label: "Fonctionnalités", href: "/#fonctionnalites" },
    { label: "Tarifs", href: "/tarifs" },
    { label: "Cas d'utilisation", href: "/#a-qui-sadresse" },
    { label: "Affiliation", href: "/affiliation" },
  ];

  const resourceLinks = [
    { label: "Blog", href: "/blog" },
    { label: "Documentation", href: "https://aide.unotif.com/documentation", external: true },
    { label: "Communauté", href: "https://www.facebook.com/groups/unotif", external: true },
    { label: "Centre d'aide", href: "https://aide.unotif.com/", external: true },
  ];

  const legalLinks = [
    { label: "Contact", href: "/contact" },
    { label: "Politique de confidentialité", href: "/privacy" },
    { label: "Conditions d'utilisation", href: "/legal" },
    { label: "Conditions du programme d'affiliation", href: "/conditions-affiliation" },
  ];

  const renderLink = (link: { label: string; href: string | null; external?: boolean }) => {
    if (!link.href) {
      return (
        <span className="text-sm font-medium text-[#074155]">
          {link.label}
        </span>
      );
    }

    // External links (open in new window)
    if (link.external) {
      return (
        <a 
          href={link.href} 
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-[#074155] hover:text-[#0C94A6] transition-colors"
        >
          {link.label}
        </a>
      );
    }

    // Internal routes starting with "/"
    if (link.href.startsWith('/')) {
      return (
        <Link 
          to={link.href} 
          className="text-sm font-medium text-[#074155] hover:text-[#0C94A6] transition-colors"
        >
          {link.label}
        </Link>
      );
    }

    // Anchor links
    return (
      <a 
        href={link.href} 
        className="text-sm font-medium text-[#074155] hover:text-[#0C94A6] transition-colors"
      >
        {link.label}
      </a>
    );
  };

  return (
    <footer className="bg-white shadow-[0_-4px_20px_-4px_rgba(0,0,0,0.08),0_-2px_8px_-2px_rgba(0,0,0,0.04)]">
      {/* SECTION 1 - Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-6">
          {/* Colonne 1 - Logo + Texte (plus large) */}
          <div className="lg:col-span-5 md:col-span-2">
            <img 
              src={logoUnotif} 
              alt="Unotif" 
              className="h-8 lg:h-10 mb-4" 
            />
            <p className="text-[13px] sm:text-sm font-medium leading-relaxed text-[#074155] max-w-sm">
              Votre canal de communication directe avec votre audience grâce aux cartes Wallet et aux notifications push.
            </p>
          </div>

          {/* Colonne 2 - Produit */}
          <div className="lg:col-span-2">
            <h4 className="text-base font-semibold text-[#022226] mb-4">
              Produit
            </h4>
            <ul className="space-y-3">
              {productLinks.map((link, index) => (
                <li key={index}>
                  {renderLink(link)}
                </li>
              ))}
            </ul>
          </div>

          {/* Colonne 3 - Ressources */}
          <div className="lg:col-span-2">
            <h4 className="text-base font-semibold text-[#022226] mb-4">
              Ressources
            </h4>
            <ul className="space-y-3">
              {resourceLinks.map((link, index) => (
                <li key={index}>
                  {renderLink(link)}
                </li>
              ))}
            </ul>
          </div>

          {/* Colonne 4 - Legal */}
          <div className="lg:col-span-3">
            <h4 className="text-base font-semibold text-[#022226] mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  {renderLink(link)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* SECTION 2 - Sub-footer (Copyright) */}
      <div className="border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Gauche - Copyright */}
            <p className="text-sm font-medium text-[#074155]">
              © <a 
                href="https://unotif.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-[#0C94A6] transition-colors"
              >
                Unotif
              </a>. Tous droits réservés
            </p>

            {/* Droite - Icônes sociales */}
            <div className="flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/company/unotif/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <img 
                  src={linkedinIcon} 
                  alt="LinkedIn" 
                  className="w-8 h-8" 
                  style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.12)) drop-shadow(0 2px 4px rgba(0,0,0,0.08))' }} 
                />
              </a>
              <a 
                href="https://www.facebook.com/groups/unotif" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <img 
                  src={facebookIcon} 
                  alt="Facebook" 
                  className="w-8 h-8" 
                  style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.12)) drop-shadow(0 2px 4px rgba(0,0,0,0.08))' }} 
                />
              </a>
              <a 
                href="https://www.youtube.com/@unotif" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <img 
                  src={youtubeIcon} 
                  alt="YouTube" 
                  className="w-8 h-8" 
                  style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.12)) drop-shadow(0 2px 4px rgba(0,0,0,0.08))' }} 
                />
              </a>
              <Link to="/contact">
                <img 
                  src={emailIcon} 
                  alt="Email" 
                  className="w-8 h-8" 
                  style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.12)) drop-shadow(0 2px 4px rgba(0,0,0,0.08))' }} 
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
